import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { Sponsoring, SponsoringStage } from "../types";

export type SponsoringsByStage = Record<Sponsoring["stage"], Sponsoring[]>;

/**
 * The stages displayed on the kanban board: every configured stage that is
 * neither an "active" status nor a "closed" status.
 */
export const getBoardStages = ({
  sponsoringStages,
  sponsoringActiveStatuses,
  sponsoringClosedStatuses,
}: Pick<
  ConfigurationContextValue,
  "sponsoringStages" | "sponsoringActiveStatuses" | "sponsoringClosedStatuses"
>): SponsoringStage[] =>
  sponsoringStages.filter(
    (stage) =>
      !sponsoringActiveStatuses.includes(stage.value) &&
      !sponsoringClosedStatuses.includes(stage.value),
  );

/**
 * The lead stages: the funnel stages, i.e. exactly the stages of the board.
 * Named separately because the "Leads" overview and the "Leads" quick filter
 * talk about leads, not about a board.
 */
export const getLeadStages = getBoardStages;

/**
 * The stage a lead entered by hand starts in: "contact" when it exists
 * (someone is contacting the company), otherwise the first lead stage
 * (which may be "suspect": not approached yet).
 */
export const defaultLeadStage = (
  config: Parameters<typeof getBoardStages>[0],
) => {
  const stages = getLeadStages(config);
  return (
    stages.find((stage) => stage.value === "contact")?.value ?? stages[0]?.value
  );
};

export const getSponsoringsByStage = (
  unorderedSponsorings: Sponsoring[],
  stages: SponsoringStage[],
) => {
  if (!stages || stages.length === 0) return {};
  const sponsoringsByStage: SponsoringsByStage = unorderedSponsorings.reduce(
    (acc, sponsoring) => {
      // if the sponsoring has a stage that does not exist in configuration, assign it to the first stage
      const stage = stages.find((s) => s.value === sponsoring.stage)
        ? sponsoring.stage
        : stages[0].value;
      acc[stage].push(sponsoring);
      return acc;
    },
    stages.reduce(
      (obj, stage) => ({ ...obj, [stage.value]: [] }),
      {} as SponsoringsByStage,
    ),
  );
  // order each column by index
  stages.forEach((stage) => {
    sponsoringsByStage[stage.value] = sponsoringsByStage[stage.value].sort(
      (recordA: Sponsoring, recordB: Sponsoring) =>
        recordA.index - recordB.index,
    );
  });
  return sponsoringsByStage;
};

/**
 * Every configured stage that is not a "closed" status: the funnel stages
 * plus the active ones. This is what the list view shows by default.
 */
export const getOpenStages = ({
  sponsoringStages,
  sponsoringClosedStatuses,
}: Pick<
  ConfigurationContextValue,
  "sponsoringStages" | "sponsoringClosedStatuses"
>): SponsoringStage[] =>
  sponsoringStages.filter(
    (stage) => !sponsoringClosedStatuses.includes(stage.value),
  );

/**
 * Build a PostgREST "in" filter value from a list of stages, e.g. "(a,b,c)".
 */
export const toStageInFilter = (stages: Array<SponsoringStage | string>) =>
  `(${stages
    .map((stage) => (typeof stage === "string" ? stage : stage.value))
    .join(",")})`;
